import { __awaiter, __generator } from "tslib";
import { validate as uuidValidate } from 'uuid';
import { PermissionStatus } from '../interfaces';
export var Permission;
(function (Permission) {
    var ErrorCode;
    (function (ErrorCode) {
        ErrorCode[ErrorCode["RECORD_DOESNT_EXIST"] = 5500] = "RECORD_DOESNT_EXIST";
        ErrorCode[ErrorCode["INDEXEDDB_INTERNAL_ERROR"] = 5600] = "INDEXEDDB_INTERNAL_ERROR";
        ErrorCode[ErrorCode["INVALID_SEARCH_PARAMETERS"] = 5700] = "INVALID_SEARCH_PARAMETERS";
        ErrorCode[ErrorCode["BLOCKED"] = 5800] = "BLOCKED";
    })(ErrorCode = Permission.ErrorCode || (Permission.ErrorCode = {}));
    var indexID;
    (function (indexID) {
        indexID["APPID_AND_USERID_INDEX"] = "appID_and_userID";
        indexID["APPID_INDEX"] = "appID";
        indexID["USERID_INDEX"] = "userID";
    })(indexID = Permission.indexID || (Permission.indexID = {}));
    var sharedDBVersionConstant = 1;
    var appObjectStore = 'apps';
    var databaseName = 'appsPermissionsDB';
    /**
     *
     * @param appID AppID for an app
     * @param userID UserID for a user
     * @returns Promise with store and storeIndex.
     */
    function initializeDB(appID, userID) {
        return new Promise(function (resolve, reject) {
            var request = indexedDB.open(databaseName, sharedDBVersionConstant);
            var dbError;
            request.onblocked = function () {
                dbError = {
                    errorCode: ErrorCode.BLOCKED,
                    message: 'Please close all the existing tabs.',
                };
                reject(dbError);
            };
            request.onupgradeneeded = function () {
                var db = request.result;
                if (!db.objectStoreNames.contains(appObjectStore)) {
                    var createAppsObjectStore = db.createObjectStore(appObjectStore, {
                        keyPath: ['appID', 'userID'],
                        autoIncrement: false,
                    });
                    createAppsObjectStore.createIndex(indexID.APPID_AND_USERID_INDEX, ['appID', 'userID'], { unique: true });
                    createAppsObjectStore.createIndex(indexID.APPID_INDEX, 'appID', { unique: false });
                    createAppsObjectStore.createIndex(indexID.USERID_INDEX, 'userID', { unique: false });
                }
            };
            request.onerror = function (error) {
                dbError = {
                    errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                    message: error.type,
                };
                reject(dbError);
            };
            request.onsuccess = function () {
                var db = request.result;
                var transactions = db.transaction(appObjectStore, 'readwrite');
                var store = transactions.objectStore(appObjectStore);
                var storeIndex = store.index(indexID.APPID_AND_USERID_INDEX);
                var getRequest = storeIndex.get([appID, userID]);
                getRequest.onsuccess = function () {
                    resolve([store, getRequest.result]);
                };
                getRequest.onerror = function (error) {
                    dbError = {
                        errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                        message: error.type,
                    };
                    reject(dbError);
                };
                db.close();
            };
        });
    }
    /**
     * Checks if the record exists or not.
     * @param appID AppID for an app
     * @param userID UserID for a user
     * @returns boolean value
     */
    function doesRecordExist(appID, userID) {
        var _this = this;
        return new Promise(function (resolve, reject) { return __awaiter(_this, void 0, void 0, function () {
            var isValidAppID, dbError, store;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        isValidAppID = uuidValidate(appID);
                        if (!isValidAppID) {
                            dbError = {
                                errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                                message: 'AppID is not valid. Must be GUID format',
                            };
                            reject(dbError);
                        }
                        if (!userID) {
                            dbError = {
                                errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                                message: 'UserID should not be null/empty.',
                            };
                            reject(dbError);
                        }
                        return [4 /*yield*/, initializeDB(appID, userID)];
                    case 1:
                        store = _a.sent();
                        try {
                            resolve(store[1] !== undefined);
                        }
                        catch (error) {
                            dbError = {
                                errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                                message: error.type,
                            };
                            reject(dbError);
                        }
                        return [2 /*return*/];
                }
            });
        }); });
    }
    Permission.doesRecordExist = doesRecordExist;
    /**
     * Creates a record in IndexedDB
     * @param appID AppID for an app
     * @param userID UserID for a user
     * @returns Promise string.
     */
    function createRecord(appID, userID) {
        var _this = this;
        return new Promise(function (resolve, reject) { return __awaiter(_this, void 0, void 0, function () {
            var isValidAppID, dbError, _a, store, result, defaultPermissionsPayload, addData;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        isValidAppID = uuidValidate(appID);
                        if (!isValidAppID) {
                            dbError = {
                                errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                                message: 'AppID is not valid. Must be GUID format',
                            };
                            reject(dbError);
                        }
                        if (!userID) {
                            dbError = {
                                errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                                message: 'UserID should not be null/empty.',
                            };
                            reject(dbError);
                        }
                        return [4 /*yield*/, initializeDB(appID, userID)];
                    case 1:
                        _a = _b.sent(), store = _a[0], result = _a[1];
                        defaultPermissionsPayload = {
                            appID: appID,
                            userID: userID,
                            permissions: {
                                geolocation: PermissionStatus.NeverAsked,
                                media: PermissionStatus.NeverAsked,
                                midi: PermissionStatus.NeverAsked,
                                notifications: PermissionStatus.NeverAsked,
                                openExternal: PermissionStatus.NeverAsked,
                            },
                        };
                        try {
                            if (result === undefined) {
                                addData = store.put(defaultPermissionsPayload);
                                addData.onsuccess = function () {
                                    resolve();
                                };
                            }
                        }
                        catch (error) {
                            dbError = {
                                errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                                message: error.type,
                            };
                            reject(dbError);
                        }
                        return [2 /*return*/];
                }
            });
        }); });
    }
    Permission.createRecord = createRecord;
    /**
     * Updates the permission.
     * @param appID AppID for an app
     * @param userID UserID for a user
     * @param permissionName Permission name to be updated
     * @param permissionStatus State for the permission(consented, revoked, neverAsked)
     * @returns Promise.
     */
    function updatePermission(appID, userID, permissionName, permissionStatus) {
        var _this = this;
        return new Promise(function (resolve, reject) { return __awaiter(_this, void 0, void 0, function () {
            var dbError, isValidAppID, checkIfExistsOrNot, _a, store, result, updateRequest_1, error_1;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 3, , 4]);
                        isValidAppID = uuidValidate(appID);
                        if (!isValidAppID) {
                            dbError = {
                                errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                                message: 'AppID is not valid. Must be GUID format',
                            };
                            reject(dbError);
                        }
                        if (!userID) {
                            dbError = {
                                errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                                message: 'UserID should not be null/empty.',
                            };
                            reject(dbError);
                        }
                        return [4 /*yield*/, doesRecordExist(appID, userID)];
                    case 1:
                        checkIfExistsOrNot = _b.sent();
                        // Checks if the record exists or not.
                        // True - record found
                        // False -  no record found
                        if (!checkIfExistsOrNot) {
                            createRecord(appID, userID);
                        }
                        return [4 /*yield*/, initializeDB(appID, userID)];
                    case 2:
                        _a = _b.sent(), store = _a[0], result = _a[1];
                        if (result.permissions[permissionName] !== undefined) {
                            result.permissions[permissionName] = permissionStatus;
                            updateRequest_1 = store.put(result);
                            updateRequest_1.onsuccess = function () {
                                resolve(updateRequest_1.result);
                            };
                            updateRequest_1.onerror = function (error) {
                                dbError = {
                                    errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                                    message: error.type,
                                };
                                reject(dbError);
                            };
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        error_1 = _b.sent();
                        dbError = {
                            errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                            message: error_1.type,
                        };
                        reject(dbError);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        }); });
    }
    Permission.updatePermission = updatePermission;
    /**
     * To delete the single record from database.
     *
     * @param appID - Application ID for an app.
     * @param userID - User ID for a user.
     * @returns Promise
     */
    function deleteSingleRecord(appID, userID) {
        var _this = this;
        return new Promise(function (resolve, reject) { return __awaiter(_this, void 0, void 0, function () {
            var dbError, isValidAppID, store_1, getRequest_1, error_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        isValidAppID = uuidValidate(appID);
                        if (!isValidAppID) {
                            dbError = {
                                errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                                message: 'AppID is not valid. Must be GUID format',
                            };
                            reject(dbError);
                        }
                        if (!userID) {
                            dbError = {
                                errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                                message: 'UserID should not be null/empty.',
                            };
                            reject(dbError);
                        }
                        return [4 /*yield*/, initializeDB(appID, userID)];
                    case 1:
                        store_1 = _a.sent();
                        getRequest_1 = store_1[0]
                            .index(indexID.APPID_AND_USERID_INDEX)
                            .openKeyCursor(IDBKeyRange.only([appID, userID]));
                        getRequest_1.onsuccess = function () {
                            var cursor = getRequest_1.result;
                            if (cursor) {
                                store_1[0].delete(cursor.primaryKey);
                                resolve();
                            }
                            else {
                                dbError = {
                                    errorCode: ErrorCode.RECORD_DOESNT_EXIST,
                                    message: "Record doesn't exists.",
                                };
                                reject(dbError);
                            }
                        };
                        getRequest_1.onerror = function (error) {
                            dbError = {
                                errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                                message: error.type,
                            };
                            reject(dbError);
                        };
                        return [3 /*break*/, 3];
                    case 2:
                        error_2 = _a.sent();
                        dbError = {
                            errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                            message: error_2.type,
                        };
                        reject(dbError);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        }); });
    }
    Permission.deleteSingleRecord = deleteSingleRecord;
    function searchRecord(appID, userID, searchByIndex) {
        var _this = this;
        return new Promise(function (resolve, reject) { return __awaiter(_this, void 0, void 0, function () {
            var getRequest, dbError, isValidAppID, store, error_3;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!Object.values(indexID).includes(searchByIndex)) return [3 /*break*/, 5];
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        isValidAppID = uuidValidate(appID);
                        if (!isValidAppID) {
                            dbError = {
                                errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                                message: 'AppID is not valid. Must be GUID format',
                            };
                            reject(dbError);
                        }
                        if (!userID) {
                            dbError = {
                                errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                                message: 'UserID should not be null/empty.',
                            };
                            reject(dbError);
                        }
                        return [4 /*yield*/, initializeDB(appID, userID)];
                    case 2:
                        store = _a.sent();
                        if (searchByIndex === indexID.APPID_AND_USERID_INDEX) {
                            getRequest = store[0].index(indexID.APPID_AND_USERID_INDEX).getAll([appID, userID]);
                        }
                        else if (searchByIndex === indexID.APPID_INDEX) {
                            getRequest = store[0].index(indexID.APPID_INDEX).getAll(appID);
                        }
                        else {
                            getRequest = store[0].index(indexID.USERID_INDEX).getAll(userID);
                        }
                        getRequest.onsuccess = function () {
                            var cursor = getRequest.result;
                            if (cursor) {
                                resolve(cursor);
                            }
                            else {
                                dbError = {
                                    errorCode: ErrorCode.RECORD_DOESNT_EXIST,
                                    message: "Record doesn't exists.",
                                };
                                reject(dbError);
                            }
                        };
                        getRequest.onerror = function (error) {
                            dbError = {
                                errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                                message: error.type,
                            };
                            reject(dbError);
                        };
                        return [3 /*break*/, 4];
                    case 3:
                        error_3 = _a.sent();
                        dbError = {
                            errorCode: ErrorCode.INDEXEDDB_INTERNAL_ERROR,
                            message: error_3.type,
                        };
                        reject(dbError);
                        return [3 /*break*/, 4];
                    case 4: return [3 /*break*/, 6];
                    case 5:
                        dbError = {
                            errorCode: ErrorCode.INVALID_SEARCH_PARAMETERS,
                            message: "searchByIndex parameter should be one of these ".concat([
                                indexID.APPID_AND_USERID_INDEX,
                                indexID.APPID_INDEX,
                                indexID.USERID_INDEX,
                            ]),
                        };
                        reject(dbError);
                        _a.label = 6;
                    case 6: return [2 /*return*/];
                }
            });
        }); });
    }
    Permission.searchRecord = searchRecord;
    /**
     * Deletes entire object store.
     */
    function deleteObjectStore() {
        return new Promise(function (resolve, reject) {
            var req = indexedDB.deleteDatabase(databaseName);
            req.onsuccess = function () {
                resolve();
            };
            req.onerror = function (error) {
                reject(error);
            };
        });
    }
    Permission.deleteObjectStore = deleteObjectStore;
})(Permission || (Permission = {}));
//# sourceMappingURL=devicePermissionsIndexedDBStorage.js.map